var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Grid, styled } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { PlanTypeCategory } from '../../enum';
import { useTerminologyTranslation } from '../app/terminology';
import { Autocomplete, FormField, getLOBName, getUnassignedLOB, renderGroupedOptions, sortByLOBName } from '../common';
import { QueueType } from '../multiskill/types';
import { useReportAPI } from '../report/selectors';
import { ALL_DAILY_FILTER_VALUE, HeadcountType, UNASSIGNED_DAILY_LOB_VALUE, UNASSIGNED_DAILY_SUB_LOB_VALUE, getDefaultDailyChartMetric, useDailyContext } from './context';
import { toCombinedLobFilterValue, useBaselineQueues, useDailyAPI, useDailyChartLobs, useDailyChartQueueIds, useDailyChartQueues, useDailyReportPlan, useWAPQueuesAPI } from './selectors';
var StyledForm = styled(Box)(function (_a) {
  var theme = _a.theme;
  return {
    display: 'flex',
    alignItems: 'end',
    backgroundColor: theme.palette.primary.light,
    padding: theme.spacing(8),
    borderRadius: theme.shape.borderRadius
  };
});
var toOptionalNumber = function (value) {
  if (!value) return undefined;
  var parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};
var isDailyCombinedLobFilterValue = function (value) {
  return /^lob:(\d+|uncategorized)\|sub:(\d+|none)$/.test(value);
};
export var toDailyFilterValue = function (value) {
  if (!value) return undefined;
  if (value === ALL_DAILY_FILTER_VALUE || value === UNASSIGNED_DAILY_LOB_VALUE || value === UNASSIGNED_DAILY_SUB_LOB_VALUE || isDailyCombinedLobFilterValue(value)) {
    return value;
  }
  return toOptionalNumber(value);
};
export var firstNonAllOption = function (options) {
  var _a;
  return (_a = options.find(function (option) {
    return option.id !== ALL_DAILY_FILTER_VALUE;
  })) === null || _a === void 0 ? void 0 : _a.id;
};
var shouldApplyDefaultFilter = function (options, value) {
  return !options.some(function (option) {
    return option.id === value;
  });
};
export var setOptionalFilterParam = function (params, key, value) {
  if (value === undefined) {
    params.delete(key);
    return;
  }
  params.set(key, "".concat(value));
};
export var DailyForm = function (_a) {
  var _b, _c, _d;
  var viewType = _a.viewType,
    onReady = _a.onReady;
  var t = useTerminologyTranslation(['report', 'translation']).t;
  var _e = useSearchParams(),
    searchParams = _e[0],
    setSearchParams = _e[1];
  var _f = useDailyContext(),
    state = _f[0],
    setState = _f[1];
  var previousUniqueId = useRef();
  var dailyReportData = useDailyReportPlan();
  var reportData = useReportAPI()[0];
  var _g = useWAPQueuesAPI(),
    queuesData = _g[0],
    fetchQueueData = _g[1],
    clearQueuesData = _g[2];
  // Options contain an All placeholder even while the WAP cache is empty.
  // Validate selections only after both sources of scenario/queue options have loaded.
  var selectionOptionsReady = !!reportData.data && !reportData.requesting && Array.isArray(queuesData.data) && !queuesData.requesting;
  var isProdHoursPlan = ((_b = reportData.data) === null || _b === void 0 ? void 0 : _b.planTypeCategory) === PlanTypeCategory.ProdHours;
  var requestedUniqueId = searchParams.get('uniqueId');
  var resolvedUniqueId = useMemo(function () {
    var _a, _b;
    var preferredUniqueId = requestedUniqueId !== null && requestedUniqueId !== void 0 ? requestedUniqueId : state === null || state === void 0 ? void 0 : state.uniqueId;
    if (!selectionOptionsReady) return preferredUniqueId;
    if (!(dailyReportData === null || dailyReportData === void 0 ? void 0 : dailyReportData.length)) return undefined;
    return (_b = (_a = dailyReportData.find(function (plan) {
      return plan.uniqueId === preferredUniqueId;
    })) === null || _a === void 0 ? void 0 : _a.uniqueId) !== null && _b !== void 0 ? _b : dailyReportData[0].uniqueId;
  }, [dailyReportData, requestedUniqueId, selectionOptionsReady, state === null || state === void 0 ? void 0 : state.uniqueId]);
  var initialValues = useMemo(function () {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    return {
      chartLobId: (_a = toDailyFilterValue(searchParams.get('chartLobId'))) !== null && _a !== void 0 ? _a : state === null || state === void 0 ? void 0 : state.chartLobId,
      chartSubLobId: (_b = toDailyFilterValue(searchParams.get('chartSubLobId'))) !== null && _b !== void 0 ? _b : state === null || state === void 0 ? void 0 : state.chartSubLobId,
      chartQueueId: (_c = toDailyFilterValue(searchParams.get('chartQueueId'))) !== null && _c !== void 0 ? _c : state === null || state === void 0 ? void 0 : state.chartQueueId,
      headcount: 0,
      headcountType: HeadcountType.Reduction,
      viewType: viewType,
      activeDate: (_d = searchParams.get('selectedDate')) !== null && _d !== void 0 ? _d : undefined,
      chartMetric: getDefaultDailyChartMetric(isProdHoursPlan),
      startMonth: (_f = (_e = toOptionalNumber(searchParams.get('startMonth'))) !== null && _e !== void 0 ? _e : toOptionalNumber(searchParams.get('month'))) !== null && _f !== void 0 ? _f : 0,
      endMonth: (_h = (_g = toOptionalNumber(searchParams.get('endMonth'))) !== null && _g !== void 0 ? _g : toOptionalNumber(searchParams.get('month'))) !== null && _h !== void 0 ? _h : 0
    };
  }, [isProdHoursPlan, searchParams, state === null || state === void 0 ? void 0 : state.chartLobId, state === null || state === void 0 ? void 0 : state.chartQueueId, state === null || state === void 0 ? void 0 : state.chartSubLobId, viewType]);
  var _h = useForm({
      defaultValues: initialValues
    }),
    control = _h.control,
    handleSubmit = _h.handleSubmit,
    setValue = _h.setValue;
  var _j = useWatch({
      control: control,
      name: ['chartLobId', 'chartSubLobId', 'chartQueueId']
    }),
    chartLobId = _j[0],
    chartSubLobId = _j[1],
    chartQueueId = _j[2];
  var contextUniqueIdChanged = previousUniqueId.current !== undefined && previousUniqueId.current !== (state === null || state === void 0 ? void 0 : state.uniqueId);
  var queueScopeUniqueId = contextUniqueIdChanged ? state === null || state === void 0 ? void 0 : state.uniqueId : (_d = (_c = resolvedUniqueId !== null && resolvedUniqueId !== void 0 ? resolvedUniqueId : state === null || state === void 0 ? void 0 : state.uniqueId) !== null && _c !== void 0 ? _c : requestedUniqueId) !== null && _d !== void 0 ? _d : undefined;
  var baselineQueues = useBaselineQueues(queueScopeUniqueId);
  var lobs = useDailyChartLobs(queueScopeUniqueId);
  var effectiveChartLobId = useMemo(function () {
    return lobs.some(function (lob) {
      return lob.id === chartLobId;
    }) ? chartLobId : ALL_DAILY_FILTER_VALUE;
  }, [chartLobId, lobs]);
  var effectiveChartSubLobId = effectiveChartLobId === ALL_DAILY_FILTER_VALUE ? ALL_DAILY_FILTER_VALUE : chartSubLobId;
  var queues = useDailyChartQueues(effectiveChartLobId, effectiveChartSubLobId, queueScopeUniqueId);
  var unassignedLob = getUnassignedLOB();
  var requestedLobId = toDailyFilterValue(searchParams.get('chartLobId'));
  var requestedQueueId = toDailyFilterValue(searchParams.get('chartQueueId'));
  var derivedLobId = useMemo(function () {
    if (typeof requestedQueueId !== 'number') return undefined;
    var queue = baselineQueues.find(function (q) {
      return q.id === requestedQueueId;
    });
    return queue ? toCombinedLobFilterValue(queue) : undefined;
  }, [baselineQueues, requestedQueueId]);
  var effectiveChartQueueId = useMemo(function () {
    var _a;
    if (chartQueueId === ALL_DAILY_FILTER_VALUE) return chartQueueId;
    if (typeof chartQueueId === 'number' && baselineQueues.some(function (queue) {
      return queue.id === chartQueueId;
    })) {
      return chartQueueId;
    }
    return (_a = firstNonAllOption(queues)) !== null && _a !== void 0 ? _a : ALL_DAILY_FILTER_VALUE;
  }, [baselineQueues, chartQueueId, queues]);
  var chartQueueIds = useDailyChartQueueIds(effectiveChartLobId, effectiveChartSubLobId, effectiveChartQueueId, queueScopeUniqueId);
  var dailyData = useDailyAPI()[0];
  var isFirstLoad = useRef(true);
  // Fetch queues for the baseline plan
  useEffect(function () {
    fetchQueueData();
    return clearQueuesData;
  }, [clearQueuesData, fetchQueueData]);
  // Keep the explicit LOB selection. Derive it from the queue for older links without a LOB.
  useEffect(function () {
    var _a;
    if (!selectionOptionsReady || !lobs.length) return;
    var preferredLobId = isFirstLoad.current ? (_a = requestedLobId !== null && requestedLobId !== void 0 ? requestedLobId : derivedLobId) !== null && _a !== void 0 ? _a : chartLobId : chartLobId;
    if (shouldApplyDefaultFilter(lobs, preferredLobId)) {
      var defaultLobId = firstNonAllOption(lobs);
      if (chartLobId !== defaultLobId) setValue('chartLobId', defaultLobId);
    } else if (preferredLobId !== chartLobId) {
      setValue('chartLobId', preferredLobId);
    }
  }, [derivedLobId, lobs, chartLobId, requestedLobId, selectionOptionsReady, setValue]);
  // 2. Set/update chart queue when chart LOB changes.
  useEffect(function () {
    if (selectionOptionsReady && chartLobId !== undefined && queues.length) {
      if (shouldApplyDefaultFilter(queues, chartQueueId)) {
        setValue('chartQueueId', firstNonAllOption(queues));
      }
    }
  }, [chartLobId, chartSubLobId, queues, chartQueueId, selectionOptionsReady, setValue]);
  // 3. Trigger initial context load when the first selection is complete
  useEffect(function () {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var defaultQueueId = typeof chartQueueId === 'number' ? chartQueueId : (_a = chartQueueIds[0]) !== null && _a !== void 0 ? _a : (_b = baselineQueues[0]) === null || _b === void 0 ? void 0 : _b.id;
    if (selectionOptionsReady && resolvedUniqueId && lobs.some(function (lob) {
      return lob.id === chartLobId;
    }) && queues.some(function (queue) {
      return queue.id === chartQueueId;
    }) && chartLobId !== undefined && chartQueueId !== undefined && chartQueueIds.length && isFirstLoad.current) {
      setState({
        uniqueId: resolvedUniqueId,
        queueId: defaultQueueId,
        _queueId: defaultQueueId,
        chartLobId: chartLobId,
        chartSubLobId: chartSubLobId,
        chartQueueId: chartQueueId,
        chartQueueIds: chartQueueIds,
        month: (_c = toOptionalNumber(searchParams.get('month'))) !== null && _c !== void 0 ? _c : 0,
        viewType: viewType,
        activeDate: (_d = searchParams.get('selectedDate')) !== null && _d !== void 0 ? _d : undefined,
        chartMetric: getDefaultDailyChartMetric(isProdHoursPlan),
        startMonth: (_f = (_e = toOptionalNumber(searchParams.get('startMonth'))) !== null && _e !== void 0 ? _e : toOptionalNumber(searchParams.get('month'))) !== null && _f !== void 0 ? _f : 0,
        endMonth: (_h = (_g = toOptionalNumber(searchParams.get('endMonth'))) !== null && _g !== void 0 ? _g : toOptionalNumber(searchParams.get('month'))) !== null && _h !== void 0 ? _h : 0,
        headcount: 0,
        headcountType: HeadcountType.Reduction
      });
      isFirstLoad.current = false;
      onReady === null || onReady === void 0 ? void 0 : onReady();
    }
  }, [baselineQueues, selectionOptionsReady, lobs, queues, resolvedUniqueId, chartLobId, chartSubLobId, chartQueueId, chartQueueIds, searchParams, setState, isProdHoursPlan, onReady, viewType]);
  useEffect(function () {
    if (!state || !selectionOptionsReady) return;
    var previousStateUniqueId = previousUniqueId.current;
    var viewChanged = previousStateUniqueId !== undefined && previousStateUniqueId !== state.uniqueId;
    var nextUniqueId = viewChanged ? state.uniqueId : resolvedUniqueId !== null && resolvedUniqueId !== void 0 ? resolvedUniqueId : state.uniqueId;
    var scenarioChanged = Boolean(nextUniqueId && nextUniqueId !== state.uniqueId);
    if (!viewChanged && !scenarioChanged) {
      previousUniqueId.current = state.uniqueId;
      return;
    }
    if (!chartQueueIds.length) return;
    if (!lobs.some(function (lob) {
      return lob.id === chartLobId;
    }) || !queues.some(function (queue) {
      return queue.id === chartQueueId;
    })) return;
    var selectedQueueId = typeof effectiveChartQueueId === 'number' ? effectiveChartQueueId : chartQueueIds[0];
    previousUniqueId.current = nextUniqueId;
    setState(__assign(__assign({}, state), {
      uniqueId: nextUniqueId !== null && nextUniqueId !== void 0 ? nextUniqueId : state.uniqueId,
      chartLobId: effectiveChartLobId,
      chartSubLobId: effectiveChartSubLobId,
      chartQueueId: effectiveChartQueueId,
      chartQueueIds: chartQueueIds,
      queueId: selectedQueueId,
      _queueId: selectedQueueId,
      headcount: 0
    }));
  }, [chartLobId, chartQueueId, chartQueueIds, effectiveChartLobId, effectiveChartQueueId, effectiveChartSubLobId, lobs, queues, resolvedUniqueId, setState, state, selectionOptionsReady]);
  var handleUpdate = handleSubmit(function (values) {
    var _a, _b, _c;
    if (state && resolvedUniqueId) {
      var params = new URLSearchParams(searchParams);
      params.set('uniqueId', resolvedUniqueId);
      setOptionalFilterParam(params, 'chartLobId', values.chartLobId);
      setOptionalFilterParam(params, 'chartSubLobId', values.chartSubLobId);
      setOptionalFilterParam(params, 'chartQueueId', values.chartQueueId);
      setSearchParams(params, {
        replace: true
      });
      setState(__assign(__assign({}, state), {
        queueId: typeof values.chartQueueId === 'number' ? values.chartQueueId : (_a = chartQueueIds[0]) !== null && _a !== void 0 ? _a : state.queueId,
        _queueId: typeof values.chartQueueId === 'number' ? values.chartQueueId : (_b = chartQueueIds[0]) !== null && _b !== void 0 ? _b : state._queueId,
        chartLobId: values.chartLobId,
        chartSubLobId: values.chartSubLobId,
        chartQueueId: values.chartQueueId,
        chartQueueIds: chartQueueIds,
        viewType: viewType,
        headcount: 0,
        chartMetric: (_c = state.chartMetric) !== null && _c !== void 0 ? _c : getDefaultDailyChartMetric(isProdHoursPlan)
      }));
    }
  });
  var getQueueGroupName = function (option) {
    if (option.id === ALL_DAILY_FILTER_VALUE) return '';
    if (option.queueType === QueueType.Multiskill) return t('period.multiskill', {
      defaultValue: 'Multi-skill'
    });
    return getLOBName(option.lobName, option.subLobName, unassignedLob);
  };
  var sortedQueues = useMemo(function () {
    return __spreadArray([], queues, true).sort(function (a, b) {
      var _a, _b;
      if (a.id === ALL_DAILY_FILTER_VALUE) return -1;
      if (b.id === ALL_DAILY_FILTER_VALUE) return 1;
      var aMulti = a.queueType === QueueType.Multiskill;
      var bMulti = b.queueType === QueueType.Multiskill;
      if (aMulti !== bMulti) return aMulti ? 1 : -1;
      return sortByLOBName({
        name: a.name,
        lobName: (_a = a.lobName) !== null && _a !== void 0 ? _a : undefined
      }, {
        name: b.name,
        lobName: (_b = b.lobName) !== null && _b !== void 0 ? _b : undefined
      });
    });
  }, [queues]);
  return _jsx(StyledForm, __assign({
    component: "form",
    onSubmit: handleUpdate
  }, {
    children: _jsxs(Grid, __assign({
      container: true,
      spacing: 6
    }, {
      children: [_jsx(Grid, __assign({
        item: true,
        style: {
          flex: 1
        }
      }, {
        children: _jsx(FormField, __assign({
          required: true,
          name: "chartLobId",
          label: t('daily.lob'),
          control: control
        }, {
          children: _jsx(Autocomplete, {
            options: lobs
          })
        }))
      })), _jsx(Grid, __assign({
        item: true,
        style: {
          flex: 1
        }
      }, {
        children: _jsx(FormField, __assign({
          required: true,
          name: "chartQueueId",
          label: isProdHoursPlan ? t('resourcing.queueOnly') : t('resourcing.queue'),
          control: control
        }, {
          children: _jsx(Autocomplete, {
            options: sortedQueues,
            loading: queuesData.requesting,
            groupBy: function (option) {
              return getQueueGroupName(option);
            },
            renderGroup: renderGroupedOptions
          })
        }))
      })), _jsx(Grid, __assign({
        item: true,
        sx: {
          display: 'flex',
          alignItems: 'end'
        }
      }, {
        children: _jsx(Button, __assign({
          type: dailyData.requesting ? 'button' : 'submit',
          variant: "contained"
        }, {
          children: t('translation:action.update')
        }))
      }))]
    }))
  }));
};